import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { loadVehicleTypes, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { toast } from "@/components/ui/use-toast";
import { Check, ChevronsUpDown, Car, ShieldCheck, Wrench, Gauge, Users, Umbrella, Camera, CreditCard as CreditCardIcon } from "lucide-react";
import { formatDate } from "@/lib/dates";
import { localIsoDay } from "@/lib/vehicleChecks";
import {
  loadFleetSpacingSettings,
  suggestRegoRenewal,
  suggestCofRenewal,
  type FleetSpacingSettings,
} from "@/lib/fleetSpacing";
import { useBillableUnitsGuard } from "@/hooks/useBillableUnitsGuard";
import BillableUnitsWarningDialog from "@/components/BillableUnitsWarningDialog";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import FolderTabNav from "@/components/ui/folder-tab-nav";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";


import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type FuelCardRow = {
  id: string;
  pool_number: string;
  card_type: string | null;
  pin: string | null;
  status: string | null;
};

type ActiveAssignment = {
  fuel_card_id: string;
  vehicle_number: string;
};

type EftposMachineRow = {
  id: string;
  serial_number: string;
  terminal_id: string | null;
  status: string | null;
};

type ActiveEftposAssignment = {
  eftpos_machine_id: string;
  vehicle_number: string;
};

const NO_EFTPOS_MACHINE_EFT_ID = "0000";

function stripSpaces(v: string): string {
  return v.replace(/\s+/g, "");
}

// Natural sort helper - extracts number from pool number string
const extractPoolNumber = (poolNumber: string): number => {
  const match = poolNumber.match(/Pool\s*(\d+)/i);
  return match ? parseInt(match[1], 10) : 0;
};

// Parse various date formats and convert to YYYY-MM-DD for input[type="date"]
const parseDateInput = (input: string): string => {
  if (!input) return "";
  
  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
  
  // DD/MM/YYYY or DD-MM-YYYY
  const ddmmyyyy = input.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (ddmmyyyy) {
    const [, day, month, year] = ddmmyyyy;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // DD/MM/YY or DD-MM-YY
  const ddmmyy = input.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2})$/);
  if (ddmmyy) {
    const [, day, month, yy] = ddmmyy;
    const year = parseInt(yy) > 50 ? `19${yy}` : `20${yy}`;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // Try native Date parsing as fallback
  const d = new Date(input);
  if (!isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }
  
  return input; // Return as-is if can't parse
};


export default function VehicleEditModal({
  open,
  onOpenChange,
  vehicle,
  isAdmin,
  fuelCards,
  activeAssignments,
  currentFuelCardId,
  eftposMachines,
  activeEftposAssignments,
  currentEftposMachineId,
  vehicles,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  vehicle: any | null;
  isAdmin: boolean;
  fuelCards: FuelCardRow[];
  activeAssignments: ActiveAssignment[];
  currentFuelCardId: string | null;
  eftposMachines: EftposMachineRow[];
  activeEftposAssignments: ActiveEftposAssignment[];
  currentEftposMachineId: string | null;
  /** Full fleet list, used to spread Rego/COF renewals evenly across allowed
   *  months instead of clustering -- see src/lib/fleetSpacing.ts. */
  vehicles?: any[];
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const { checkChange: checkBillableUnitsChange, dialogProps: billableUnitsDialogProps } = useBillableUnitsGuard();
  const [activeTab, setActiveTab] = useState("fleet");
  const [cardPopoverOpen, setCardPopoverOpen] = useState(false);
  const [machinePopoverOpen, setMachinePopoverOpen] = useState(false);

  const [form, setForm] = useState<any>({});
  const [selectedCardId, setSelectedCardId] = useState<string>("");
  const [selectedMachineId, setSelectedMachineId] = useState<string>("");
  const [rucInactive, setRucInactive] = useState(false);
  const [serviceInactive, setServiceInactive] = useState(false);
  const [tyreInactive, setTyreInactive] = useState(false);

  // Track original values to detect changes
  const [originalValues, setOriginalValues] = useState<any>({});
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);

  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);
  }, []);

  const [spacingSettings, setSpacingSettings] = useState<FleetSpacingSettings | null>(null);
  useEffect(() => {
    if (!open) return;
    loadFleetSpacingSettings().then(setSpacingSettings);
  }, [open]);

  // Suggested renewal length/date so Rego/COF outgoings spread evenly across
  // the allowed months instead of clustering -- see src/lib/fleetSpacing.ts.
  const regoSuggestion = useMemo(() => {
    if (!spacingSettings || !vehicle || form.is_contractor_vehicle) return null;
    return suggestRegoRenewal(form.rego_date || null, vehicles || [], spacingSettings.regoAllowedMonths, vehicle.vehicle_number);
  }, [spacingSettings, vehicles, vehicle, form.rego_date, form.is_contractor_vehicle]);

  const cofSuggestion = useMemo(() => {
    if (!spacingSettings || !vehicle || !form.cof_date || form.is_contractor_vehicle) return null;
    const intervalMonths = form.cof_interval_months ? Number(form.cof_interval_months) : spacingSettings.cofDefaultIntervalMonths;
    return suggestCofRenewal(
      form.cof_date || null,
      intervalMonths,
      vehicles || [],
      spacingSettings.cofAllowedMonths,
      spacingSettings.cofFlexMonths,
      vehicle.vehicle_number,
    );
  }, [spacingSettings, vehicles, vehicle, form.cof_date, form.cof_interval_months, form.is_contractor_vehicle]);

  useEffect(() => {
    if (!open || !vehicle) return;
    // Check if values are -1 (inactive/N/A)
    const isRucInactive = vehicle.ruc_max === -1;
    const isServiceInactive = vehicle.service_due === -1;
    const isTyreInactive = vehicle.tyre_rotation_due === -1;
    
    setRucInactive(isRucInactive);
    setServiceInactive(isServiceInactive);
    setTyreInactive(isTyreInactive);
    
    const formData = {
      vehicle_number: vehicle.vehicle_number ?? "",
      registration: vehicle.registration ?? "",
      make: vehicle.make ?? "",
      model: vehicle.model ?? "",
      year: vehicle.year ?? "",
      cof_date: vehicle.cof_date ?? "",
      rego_date: vehicle.rego_date ?? "",
      cof_interval_months: vehicle.cof_interval_months ?? "",
      ruc_max: isRucInactive ? "" : (vehicle.ruc_max ?? ""),
      service_due: isServiceInactive ? "" : (vehicle.service_due ?? ""),
      tyre_rotation_due: isTyreInactive ? "" : (vehicle.tyre_rotation_due ?? ""),
      vehicle_type: vehicle.vehicle_type ?? "",
      transmission_type: vehicle.transmission_type ?? "",
      status: vehicle.status ?? "active",
      seat_capacity: vehicle.seat_capacity ?? "",
      wheelchair_capacity: vehicle.wheelchair_capacity ?? "",
      luggage_capacity: vehicle.luggage_capacity ?? "",
      wc_onboard: vehicle.wc_onboard ?? false,
      has_carseat: vehicle.has_carseat ?? false,
      has_booster: vehicle.has_booster ?? false,
      has_paywave: vehicle.has_paywave ?? false,
      is_tgb_vehicle: vehicle.is_tgb_vehicle ?? false,
      has_500kg_hoist: vehicle.has_500kg_hoist ?? false,
      is_contractor_vehicle: vehicle.is_contractor_vehicle ?? false,
      has_current_wof: vehicle.has_current_wof ?? true,
      has_camera: vehicle.has_camera ?? true,
      camera_type: vehicle.camera_type ?? "",
      camera_storage: vehicle.camera_storage ?? "",
      camera_id: vehicle.camera_id ?? "",
      admin_inspection_interval_months: vehicle.admin_inspection_interval_months ?? "",
      camera_check_interval_months: vehicle.camera_check_interval_months ?? "",
      insured_value: vehicle.insured_value ?? "",
      insurance_body_type: vehicle.insurance_body_type ?? "",
      insurance_vehicle_class: vehicle.insurance_vehicle_class ?? "",
      modification_details: vehicle.modification_details ?? "",
      finance_details: vehicle.finance_details ?? "",
      other_insurance_details: vehicle.other_insurance_details ?? "",
      manual_km: vehicle.manual_km ?? "",
      manual_km_date: vehicle.manual_km_date ?? "",
      // Booking dates
      cof_booked_for: vehicle.cof_booked_for ?? "",
      registration_ordered: vehicle.registration_ordered ?? "",
      ruc_ordered: vehicle.ruc_ordered ?? "",
      service_booked_for: vehicle.service_booked_for ?? "",
      tyre_rotation_booked_for: vehicle.tyre_rotation_booked_for ?? "",
      // Booked maintenance window (vehicle remains available until the from date)
      maintenance_from: vehicle.maintenance_from ?? "",
      maintenance_to: vehicle.maintenance_to ?? "",
    };


    
    setForm(formData);
    setOriginalValues({
      insured_value: vehicle.insured_value ?? "",
      cof_date: vehicle.cof_date ?? "",
      rego_date: vehicle.rego_date ?? "",
      ruc_max: isRucInactive ? "" : (vehicle.ruc_max ?? ""),
      service_due: isServiceInactive ? "" : (vehicle.service_due ?? ""),
      tyre_rotation_due: isTyreInactive ? "" : (vehicle.tyre_rotation_due ?? ""),
    });
    setSelectedCardId(currentFuelCardId ?? "");
    setSelectedMachineId(currentEftposMachineId ?? "");
  }, [open, vehicle, currentFuelCardId, currentEftposMachineId]);

  const assignedVehicleForCard = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of activeAssignments) map.set(a.fuel_card_id, a.vehicle_number);
    return map;
  }, [activeAssignments]);

  const selectableCards = useMemo(() => {
    // allow:
    // - unassigned active cards
    // - OR currently assigned to this vehicle
    const vno = vehicle?.vehicle_number;
    return fuelCards
      .filter((c) => c.status === "active")
      .filter((c) => {
        const assignedTo = assignedVehicleForCard.get(c.id);
        if (!assignedTo) return true;
        if (assignedTo === vno) return true;
        return false;
      })
      // Sort numerically by pool number
      .sort((a, b) => extractPoolNumber(a.pool_number) - extractPoolNumber(b.pool_number));
  }, [fuelCards, assignedVehicleForCard, vehicle]);

  // Get display text for selected card
  const selectedCardDisplay = useMemo(() => {
    if (!selectedCardId) return "No fuel card";
    const card = fuelCards.find(c => c.id === selectedCardId);
    if (!card) return "No fuel card";
    let display = card.pool_number;
    if (card.card_type) display += ` (${card.card_type})`;
    if (card.pin) display += ` - PIN: ${card.pin}`;
    return display;
  }, [selectedCardId, fuelCards]);

  const assignedVehicleForMachine = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of activeEftposAssignments) map.set(a.eftpos_machine_id, a.vehicle_number);
    return map;
  }, [activeEftposAssignments]);

  const selectableMachines = useMemo(() => {
    const vno = vehicle?.vehicle_number;
    return eftposMachines
      .filter((m) => m.status === "active")
      .filter((m) => {
        const assignedTo = assignedVehicleForMachine.get(m.id);
        if (!assignedTo) return true;
        if (assignedTo === vno) return true;
        return false;
      })
      .sort((a, b) => a.serial_number.localeCompare(b.serial_number));
  }, [eftposMachines, assignedVehicleForMachine, vehicle]);

  const selectedMachine = useMemo(
    () => eftposMachines.find((m) => m.id === selectedMachineId) ?? null,
    [selectedMachineId, eftposMachines]
  );

  // EFT ID is derived entirely from the linked machine's Terminal ID -- no
  // free-text entry, so matching stays reliable. "0000" is the established
  // placeholder for vehicles with no machine (or a machine with no Terminal
  // ID on file yet).
  const derivedEftId = useMemo(() => {
    if (selectedMachine?.terminal_id) return stripSpaces(selectedMachine.terminal_id);
    return NO_EFTPOS_MACHINE_EFT_ID;
  }, [selectedMachine]);

  const selectedMachineDisplay = useMemo(() => {
    if (!selectedMachineId) return "No EFTPOS machine";
    if (!selectedMachine) return "No EFTPOS machine";
    let display = `SN ${selectedMachine.serial_number}`;
    display += selectedMachine.terminal_id ? ` (TID ${selectedMachine.terminal_id})` : " (no Terminal ID on file)";
    return display;
  }, [selectedMachineId, selectedMachine]);

  // Handle COF date change - reset booking if changed
  const handleCofDateChange = (value: string) => {
    const newForm = { ...form, cof_date: value };
    if (value !== originalValues.cof_date) {
      newForm.cof_booked_for = "";
    }
    setForm(newForm);
  };

  // Handle Rego date change - reset booking if changed
  const handleRegoDateChange = (value: string) => {
    const newForm = { ...form, rego_date: value };
    if (value !== originalValues.rego_date) {
      newForm.registration_ordered = "";
    }
    setForm(newForm);
  };

  // Handle RUC max change - reset booking if changed
  const handleRucMaxChange = (value: string) => {
    const newForm = { ...form, ruc_max: value };
    if (value !== originalValues.ruc_max) {
      newForm.ruc_ordered = "";
    }
    setForm(newForm);
  };

  // Handle Service due change - reset booking if changed
  const handleServiceDueChange = (value: string) => {
    const newForm = { ...form, service_due: value };
    if (value !== originalValues.service_due) {
      newForm.service_booked_for = "";
    }
    setForm(newForm);
  };

  // Handle Tyre rotation due change - reset booking if changed
  const handleTyreRotationDueChange = (value: string) => {
    const newForm = { ...form, tyre_rotation_due: value };
    if (value !== originalValues.tyre_rotation_due) {
      newForm.tyre_rotation_booked_for = "";
    }
    setForm(newForm);
  };

  const saveVehicle = async () => {
    if (!isAdmin || !vehicle) return;
    if (!form.vehicle_number || !form.registration || !form.make || !form.model || !form.year) {
      toast({
        title: "Missing required fields",
        description: "Fleet Number, Registration, Make, Model, and Year are required.",
        variant: "destructive",
      });
      return;
    }

    // Insured value (the Insurance tab's Vehicle Values table reads this same field)
    if (String(form.insured_value ?? "").trim() !== "") {
      const iv = Number(form.insured_value);
      if (!Number.isFinite(iv) || iv < 0) {
        toast({ title: "Check the insured value", description: "Enter the insured value as a number, e.g. 18500.", variant: "destructive" });
        return;
      }
    }

    // Validate booked maintenance window (both or neither, and To >= From)
    if (
      (form.maintenance_from && !form.maintenance_to) ||
      (!form.maintenance_from && form.maintenance_to)
    ) {
      toast({
        title: "Incomplete maintenance booking",
        description:
          "Please set both a Maintenance From and Maintenance To date, or clear the booking.",
        variant: "destructive",
      });
      return;
    }
    if (
      form.maintenance_from &&
      form.maintenance_to &&
      form.maintenance_to < form.maintenance_from
    ) {
      toast({
        title: "Invalid maintenance dates",
        description: "Maintenance To must be on or after Maintenance From.",
        variant: "destructive",
      });
      return;
    }

    // status drives the platform base charge's billable unit count (see
    // billableUnits.ts) -- warn before a real active <-> inactive/
    // maintenance transition, not on every save.
    const wasActive = (vehicle.status || "").trim().toLowerCase() === "active";
    const willBeActive = (form.status || "active").trim().toLowerCase() === "active";
    if (wasActive !== willBeActive) {
      const proceed = await checkBillableUnitsChange("vehicle", willBeActive);
      if (!proceed) return;
    }

    setSaving(true);
    try {
      // Handle values: -1 means inactive/N/A, null means missing, positive number is the value
      const parseKmValue = (value: any, isInactive: boolean): number | null => {
        if (isInactive) return -1;
        if (value === "" || value === null || value === undefined) return null;
        return Number(value);
      };

      const payload: any = {
        vehicle_number: form.vehicle_number,
        registration: form.registration || null,
        make: form.make || null,
        model: form.model || null,
        year: form.year ? parseInt(String(form.year), 10) : null,
        eft_id: derivedEftId,

        cof_date: form.cof_date || null,
        rego_date: form.rego_date || null,
        cof_interval_months: form.cof_interval_months === "" ? null : Number(form.cof_interval_months),

        ruc_max: parseKmValue(form.ruc_max, rucInactive),
        service_due: parseKmValue(form.service_due, serviceInactive),
        tyre_rotation_due: parseKmValue(form.tyre_rotation_due, tyreInactive),

        vehicle_type: form.vehicle_type || null,
        transmission_type: form.transmission_type || null,
        status: form.status || "active",

        seat_capacity: form.seat_capacity === "" ? null : Number(form.seat_capacity),
        wheelchair_capacity: form.wheelchair_capacity === "" ? null : Number(form.wheelchair_capacity),
        luggage_capacity: form.luggage_capacity === "" ? null : Number(form.luggage_capacity),

        wc_onboard: !!form.wc_onboard,
        has_carseat: !!form.has_carseat,
        has_booster: !!form.has_booster,
        has_paywave: !!form.has_paywave,
        is_tgb_vehicle: !!form.is_tgb_vehicle,
        has_500kg_hoist: !!form.has_500kg_hoist,
        is_contractor_vehicle: !!form.is_contractor_vehicle,
        has_current_wof: !!form.has_current_wof,

        has_camera: !!form.has_camera,
        camera_type: form.has_camera ? String(form.camera_type ?? "").trim() || null : null,
        camera_storage: form.has_camera ? form.camera_storage || null : null,
        camera_id: form.has_camera ? String(form.camera_id ?? "").trim() || null : null,
        admin_inspection_interval_months:
          form.admin_inspection_interval_months === "" ? null : Number(form.admin_inspection_interval_months),
        camera_check_interval_months:
          form.camera_check_interval_months === "" ? null : Number(form.camera_check_interval_months),

        // The one place the insured value lives: the Insurance tab's Vehicle Values table shows and edits this same field.
        insured_value: String(form.insured_value ?? "").trim() === "" ? null : Number(form.insured_value),
        ...(String(form.insured_value ?? "") !== String(originalValues.insured_value ?? "")
          ? { insured_value_updated_at: localIsoDay(new Date()) }
          : {}),
        insurance_body_type: form.insurance_body_type || null,
        insurance_vehicle_class: form.insurance_vehicle_class || null,
        modification_details: form.modification_details || null,
        finance_details: form.finance_details || null,
        other_insurance_details: form.other_insurance_details || null,


        // Manual KM fields. Editing the reading here means an admin is now
        // the source of truth for it, not a driver's inspection -- clear
        // the attribution so it goes back to reading as plain "Manual"
        // rather than showing a stale driver name. Left untouched when the
        // admin saves the form without changing this reading, so unrelated
        // edits elsewhere on the vehicle don't wipe a driver's attribution.
        manual_km: form.manual_km === "" ? null : Number(form.manual_km),
        manual_km_date: form.manual_km_date || null,
        manual_km_driver:
          String(form.manual_km ?? "") !== String(vehicle.manual_km ?? "") ||
          String(form.manual_km_date ?? "") !== String(vehicle.manual_km_date ?? "")
            ? null
            : vehicle.manual_km_driver ?? null,

        // Booking dates
        cof_booked_for: form.cof_booked_for || null,
        registration_ordered: form.registration_ordered || null,
        ruc_ordered: form.ruc_ordered || null,
        service_booked_for: form.service_booked_for || null,
        tyre_rotation_booked_for: form.tyre_rotation_booked_for || null,

        // Booked maintenance window
        maintenance_from: form.maintenance_from || null,
        maintenance_to: form.maintenance_to || null,

        
        updated_at: new Date().toISOString(),
      };

      // Use the ORIGINAL vehicle_number to identify the record
      const originalVehicleNumber = vehicle.vehicle_number;
      
      const { error: vErr } = await supabase
        .from("vehicles")
        .update(payload)
        .eq("vehicle_number", originalVehicleNumber);

      if (vErr) throw vErr;

      // Push the fields TaxiCaller also cares about (capacity, plate, make,
      // paywave, COF/Rego) straight back to TaxiCaller. DMS is authoritative
      // for these now -- this failing shouldn't block the DMS save (the data
      // is already safely saved above), so it's reported as a separate,
      // non-blocking toast rather than thrown.
      supabase.functions
        .invoke("push-vehicle-to-taxicaller", { body: { vehicle_number: form.vehicle_number } })
        .then(({ data, error: pushErr }) => {
          if (pushErr || data?.ok === false) {
            toast({
              title: "TaxiCaller sync failed",
              description: pushErr?.message ?? data?.error ?? "Could not push vehicle changes to TaxiCaller.",
              variant: "destructive",
            });
          }
        });

      // 2) Fuel card assignment (only if changed)
      // Use the NEW vehicle number for fuel card assignments
      const vno = form.vehicle_number as string;
      const before = currentFuelCardId ?? "";
      const after = selectedCardId ?? "";

      if (before !== after) {
        // unassign current active assignment for vehicle (use original vehicle number)
        await supabase
          .from("vehicle_fuel_cards")
          .update({ unassigned_at: new Date().toISOString() })
          .eq("vehicle_number", originalVehicleNumber)
          .is("unassigned_at", null);

        if (after) {
          // guard: card not assigned elsewhere (DB unique index also protects)
          const alreadyAssignedTo = assignedVehicleForCard.get(after);
          if (alreadyAssignedTo && alreadyAssignedTo !== originalVehicleNumber) {
            toast({
              title: "Fuel card already assigned",
              description: `This card is assigned to vehicle ${alreadyAssignedTo}.`,
              variant: "destructive",
            });
            setSaving(false);
            return;
          }

          // create new assignment with new vehicle number
          const { error: assignErr } = await supabase
            .from("vehicle_fuel_cards")
            .insert({
              fuel_card_id: after,
              vehicle_number: vno,
              assigned_at: new Date().toISOString(),
            });

          if (assignErr) throw assignErr;
        }
      } else if (form.vehicle_number !== originalVehicleNumber && currentFuelCardId) {
        // Vehicle number changed but fuel card assignment didn't change
        // Update the existing fuel card assignment to use the new vehicle number
        await supabase
          .from("vehicle_fuel_cards")
          .update({ vehicle_number: vno })
          .eq("vehicle_number", originalVehicleNumber)
          .is("unassigned_at", null);
      }

      // 3) EFTPOS machine assignment (only if changed) -- eft_id above was
      // already derived from whichever machine ends up selected here.
      const eftposBefore = currentEftposMachineId ?? "";
      const eftposAfter = selectedMachineId ?? "";

      if (eftposBefore !== eftposAfter) {
        await supabase
          .from("vehicle_eftpos_machines")
          .update({ unassigned_at: new Date().toISOString() })
          .eq("vehicle_number", originalVehicleNumber)
          .is("unassigned_at", null);

        if (eftposAfter) {
          const alreadyAssignedTo = assignedVehicleForMachine.get(eftposAfter);
          if (alreadyAssignedTo && alreadyAssignedTo !== originalVehicleNumber) {
            toast({
              title: "EFTPOS machine already assigned",
              description: `This machine is assigned to vehicle ${alreadyAssignedTo}.`,
              variant: "destructive",
            });
            setSaving(false);
            return;
          }

          const { error: machineAssignErr } = await supabase
            .from("vehicle_eftpos_machines")
            .insert({
              eftpos_machine_id: eftposAfter,
              vehicle_number: vno,
              assigned_at: new Date().toISOString(),
            });

          if (machineAssignErr) throw machineAssignErr;
        }
      } else if (form.vehicle_number !== originalVehicleNumber && currentEftposMachineId) {
        await supabase
          .from("vehicle_eftpos_machines")
          .update({ vehicle_number: vno })
          .eq("vehicle_number", originalVehicleNumber)
          .is("unassigned_at", null);
      }

      toast({
        title: "Vehicle updated",
        description: `${form.vehicle_number} has been updated successfully.`,
      });

      onSaved();
    } catch (e: any) {
      toast({
        title: "Error saving vehicle",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!vehicle) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Edit Vehicle: {vehicle.vehicle_number}
            <Badge variant={form.status === "active" ? "default" : "secondary"}>
              {form.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <FolderTabNav
          className="pt-2"
          tabs={[
            { value: "fleet", label: "Fleet", icon: Car },
            { value: "compliance", label: "Compliance", icon: ShieldCheck },
            { value: "maintenance", label: "Maintenance", icon: Wrench },
            { value: "odometer", label: "Odo", icon: Gauge },
            { value: "capacity", label: "Capacity", icon: Users },
            { value: "camera", label: "Camera", icon: Camera },
            { value: "insurance", label: "Insurance", icon: Umbrella },
            { value: "cards", label: "Cards", icon: CreditCardIcon },
          ]}
          value={activeTab}
          onValueChange={setActiveTab}
        />
        <div className="rounded-b-xl rounded-tr-xl border border-yellow-900/20 bg-zinc-900 p-4 sm:p-5">
          {activeTab === "fleet" && (
          <div className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vehicle_number">Fleet # *</Label>
              <Input
                id="vehicle_number"
                value={form.vehicle_number}
                onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })}
                disabled={!isAdmin}
                placeholder="e.g. 100"
              />
              {form.vehicle_number !== vehicle.vehicle_number && (
                <p className="text-xs text-amber-500">Fleet number will be changed</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="registration">Registration *</Label>
              <Input
                id="registration"
                value={form.registration}
                onChange={(e) => setForm({ ...form, registration: e.target.value })}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eft_id">EFT ID</Label>
              <Input id="eft_id" value={derivedEftId} disabled readOnly />
              <p className="text-xs text-muted-foreground">
                Set by the linked EFTPOS machine below -- not editable directly.
              </p>
            </div>
          </div>


          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="make">Make *</Label>
              <Input
                id="make"
                value={form.make}
                onChange={(e) => setForm({ ...form, make: e.target.value })}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="model">Model *</Label>
              <Input
                id="model"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="year">Year *</Label>
              <Input
                id="year"
                type="number"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                disabled={!isAdmin}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vehicle_type">Vehicle Type</Label>
              <Select
                value={form.vehicle_type || "__none__"}
                onValueChange={(v) => setForm({ ...form, vehicle_type: v === "__none__" ? "" : v })}
                disabled={!isAdmin}
              >
                <SelectTrigger id="vehicle_type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  {vehicleTypes.map((t) => (
                    <SelectItem key={t.vehicle_type} value={t.vehicle_type}>{t.vehicle_type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="transmission_type">Transmission</Label>
              <Select
                value={form.transmission_type || "__none__"}
                onValueChange={(v) => setForm({ ...form, transmission_type: v === "__none__" ? "" : v })}
                disabled={!isAdmin}
              >
                <SelectTrigger id="transmission_type">
                  <SelectValue placeholder="Select transmission" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  <SelectItem value="Automatic">Automatic</SelectItem>
                  <SelectItem value="Manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={form.status || "active"}
                onValueChange={(v) => setForm({ ...form, status: v })}
                disabled={!isAdmin}
              >
                <SelectTrigger id="status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
              {form.status === "maintenance" && (
                <p className="text-xs text-amber-500">
                  Long-term, open-ended maintenance (no end date). The vehicle stays
                  unavailable in the roster until you set the status back to Active. For a
                  fixed booked window, leave status Active and use Booked Maintenance below.
                </p>
              )}
            </div>

          </div>
          </div>
          )}
          {activeTab === "capacity" && (
          <div className="space-y-4">
          {/* Capacity */}
          <div>
            <h4 className="font-medium mb-1">Capacity</h4>
            <p className="text-xs text-muted-foreground mb-3">
              How many passengers, wheelchairs, and pieces of luggage this specific vehicle can carry — used to match
              it against a booking's requirements.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="seat_capacity">Seats</Label>
                <Input
                  id="seat_capacity"
                  type="number"
                  min={0}
                  value={form.seat_capacity}
                  onChange={(e) => setForm({ ...form, seat_capacity: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="e.g. 4"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wheelchair_capacity">Wheelchairs</Label>
                <Input
                  id="wheelchair_capacity"
                  type="number"
                  min={0}
                  value={form.wheelchair_capacity}
                  onChange={(e) => setForm({ ...form, wheelchair_capacity: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="e.g. 1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="luggage_capacity">Luggage</Label>
                <Input
                  id="luggage_capacity"
                  type="number"
                  min={0}
                  value={form.luggage_capacity}
                  onChange={(e) => setForm({ ...form, luggage_capacity: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="e.g. 2"
                />
              </div>
            </div>
          </div>

          {/* Equipment */}
          <div className="border-t pt-4 mt-2">
            <h4 className="font-medium mb-3">Equipment</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { key: "wc_onboard", label: "W/C Onboard" },
                { key: "has_carseat", label: "Carseat" },
                { key: "has_booster", label: "Booster" },
                { key: "has_paywave", label: "Paywave" },
                { key: "is_tgb_vehicle", label: "TGB Vehicle" },
                { key: "has_500kg_hoist", label: "500kg Hoist" },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox
                    id={key}
                    checked={!!form[key]}
                    onCheckedChange={(c) => setForm({ ...form, [key]: !!c })}
                    disabled={!isAdmin}
                  />
                  <Label htmlFor={key} className="text-sm cursor-pointer">{label}</Label>
                </div>
              ))}
            </div>
          </div>
          </div>
          )}
          {activeTab === "camera" && (
          <div>
          <div>
            <h4 className="font-medium mb-3">Camera &amp; regular checks</h4>
            <div className="flex items-center gap-2 mb-3">
              <Checkbox
                id="has_camera"
                checked={!!form.has_camera}
                onCheckedChange={(c) => setForm({ ...form, has_camera: !!c })}
                disabled={!isAdmin}
              />
              <Label htmlFor="has_camera" className="text-sm cursor-pointer">This vehicle has an in-car camera</Label>
            </div>
            {form.has_camera && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="camera_type">Camera type</Label>
                  <Input
                    id="camera_type"
                    list="camera-type-options"
                    value={form.camera_type ?? ""}
                    onChange={(e) => setForm({ ...form, camera_type: e.target.value })}
                    placeholder="Make / model"
                    disabled={!isAdmin}
                  />
                  <datalist id="camera-type-options">
                    {Array.from(new Set((vehicles ?? []).map((v: any) => String(v.camera_type ?? "").trim()).filter(Boolean))).map((t) => (
                      <option key={t as string} value={t as string} />
                    ))}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="camera_storage">Records to</Label>
                  <Select
                    value={form.camera_storage || "__none__"}
                    onValueChange={(v) => setForm({ ...form, camera_storage: v === "__none__" ? "" : v })}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger id="camera_storage">
                      <SelectValue placeholder="Select storage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not specified</SelectItem>
                      <SelectItem value="hard_drive">Hard drive</SelectItem>
                      <SelectItem value="sd_card">SD / storage card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="camera_id">Camera ID number</Label>
                  <Input
                    id="camera_id"
                    value={form.camera_id ?? ""}
                    onChange={(e) => setForm({ ...form, camera_id: e.target.value })}
                    disabled={!isAdmin}
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="admin_inspection_interval_months">Admin inspection every (months)</Label>
                <Input
                  id="admin_inspection_interval_months"
                  type="number"
                  min={1}
                  value={form.admin_inspection_interval_months}
                  onChange={(e) => setForm({ ...form, admin_inspection_interval_months: e.target.value })}
                  placeholder="Fleet default"
                  disabled={!isAdmin}
                />
              </div>
              {form.has_camera && (
                <div className="space-y-2">
                  <Label htmlFor="camera_check_interval_months">Camera check every (months)</Label>
                  <Input
                    id="camera_check_interval_months"
                    type="number"
                    min={1}
                    value={form.camera_check_interval_months}
                    onChange={(e) => setForm({ ...form, camera_check_interval_months: e.target.value })}
                    placeholder="Fleet default"
                    disabled={!isAdmin}
                  />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Leave the intervals blank to use the fleet default (Settings → Fleet Defaults → Fleet Checks). Record a camera check from the
              camera button on the Fleet list.
            </p>
          </div>
          </div>
          )}
          {activeTab === "insurance" && (
          <div>
          {/* Insurance Details */}
          <div>
            <h4 className="font-medium mb-3">Insurance Details</h4>
            <p className="text-xs text-muted-foreground mb-3">
              The insured value, plus extra classification details an insurance claim form asks for
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="insured_value">Insured Value ($)</Label>
                <Input
                  id="insured_value"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="1"
                  value={form.insured_value ?? ""}
                  onChange={(e) => setForm({ ...form, insured_value: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="e.g. 18500"
                />
                <p className="text-xs text-muted-foreground">
                  {vehicle?.insured_value_updated_at
                    ? "Last updated " + formatDate(vehicle.insured_value_updated_at) + ". "
                    : "Not set yet. "}
                  This is the same value as the Insurance tab's Vehicle Values table: saving it here updates that table.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="insurance_body_type">Body Type</Label>
                <Input
                  id="insurance_body_type"
                  value={form.insurance_body_type ?? ""}
                  onChange={(e) => setForm({ ...form, insurance_body_type: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="e.g. Sedan, Van"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="insurance_vehicle_class">NZTA Vehicle Class</Label>
                <Input
                  id="insurance_vehicle_class"
                  value={form.insurance_vehicle_class ?? ""}
                  onChange={(e) => setForm({ ...form, insurance_vehicle_class: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="e.g. MA"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="modification_details">Modifications (if any)</Label>
                <Input
                  id="modification_details"
                  value={form.modification_details ?? ""}
                  onChange={(e) => setForm({ ...form, modification_details: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="Leave blank if none"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="finance_details">Finance (if any)</Label>
                <Input
                  id="finance_details"
                  value={form.finance_details ?? ""}
                  onChange={(e) => setForm({ ...form, finance_details: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="Financier name, if the vehicle is financed -- leave blank if owned outright"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="other_insurance_details">Other Insurance (if any)</Label>
                <Input
                  id="other_insurance_details"
                  value={form.other_insurance_details ?? ""}
                  onChange={(e) => setForm({ ...form, other_insurance_details: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="Leave blank if none"
                />
              </div>
            </div>
          </div>
          </div>
          )}
          {activeTab === "compliance" && (
          <div>
          {/* Compliance Dates */}
          <div>
            <h4 className="font-medium mb-3">Compliance Dates</h4>
            <p className="text-xs text-muted-foreground mb-3">
              You can paste dates in formats like DD/MM/YYYY, DD-MM-YYYY, or DD/MM/YY
            </p>
            <div className="flex items-center gap-2 mb-4">
              <Checkbox
                id="is_contractor_vehicle"
                checked={!!form.is_contractor_vehicle}
                onCheckedChange={(c) => setForm({ ...form, is_contractor_vehicle: !!c })}
                disabled={!isAdmin}
              />
              <Label htmlFor="is_contractor_vehicle">
                Contractor Vehicle (company doesn't pay Rego/COF -- excluded from Fleet Spacing)
              </Label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cof_date">COF Date</Label>
                <Input
                  id="cof_date"
                  type="date"
                  value={form.cof_date}
                  onChange={(e) => handleCofDateChange(e.target.value)}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    const parsed = parseDateInput(pasted.trim());
                    if (parsed && parsed !== pasted.trim()) {
                      e.preventDefault();
                      handleCofDateChange(parsed);
                    }
                  }}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cof_booked_for">COF Booked For</Label>
                <Input
                  id="cof_booked_for"
                  type="date"
                  value={form.cof_booked_for}
                  onChange={(e) => setForm({ ...form, cof_booked_for: e.target.value })}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    const parsed = parseDateInput(pasted.trim());
                    if (parsed && parsed !== pasted.trim()) {
                      e.preventDefault();
                      setForm({ ...form, cof_booked_for: parsed });
                    }
                  }}
                  disabled={!isAdmin}
                />
                {form.cof_date !== originalValues.cof_date && !form.cof_booked_for && (
                  <p className="text-xs text-muted-foreground">Booking cleared (COF date changed)</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="cof_interval_months">COF/WOF Interval (months)</Label>
                <Input
                  id="cof_interval_months"
                  type="number"
                  min={1}
                  value={form.cof_interval_months}
                  onChange={(e) => setForm({ ...form, cof_interval_months: e.target.value })}
                  placeholder={spacingSettings ? `Default: ${spacingSettings.cofDefaultIntervalMonths}` : "Default"}
                  disabled={!isAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to use the fleet default (Settings → Fleet Defaults → COF). Set to 12 for a WOF vehicle.
                </p>
              </div>
              {cofSuggestion && (
                <div className="space-y-2">
                  <Label>Suggested Renewal</Label>
                  <div className="rounded-md border border-yellow-900/30 bg-yellow-950/20 px-3 py-2 text-sm">
                    Book for <span className="font-medium text-yellow-500">{formatDate(cofSuggestion.targetDate)}</span> ({cofSuggestion.targetLabel}) --
                    currently {cofSuggestion.targetCount} other vehicle{cofSuggestion.targetCount === 1 ? "" : "s"} due that month.
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="rego_date">Registration Date</Label>
                <Input
                  id="rego_date"
                  type="date"
                  value={form.rego_date}
                  onChange={(e) => handleRegoDateChange(e.target.value)}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    const parsed = parseDateInput(pasted.trim());
                    if (parsed && parsed !== pasted.trim()) {
                      e.preventDefault();
                      handleRegoDateChange(parsed);
                    }
                  }}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="registration_ordered">Registration Ordered</Label>
                <Input
                  id="registration_ordered"
                  type="date"
                  value={form.registration_ordered}
                  onChange={(e) => setForm({ ...form, registration_ordered: e.target.value })}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    const parsed = parseDateInput(pasted.trim());
                    if (parsed && parsed !== pasted.trim()) {
                      e.preventDefault();
                      setForm({ ...form, registration_ordered: parsed });
                    }
                  }}
                  disabled={!isAdmin}
                />
                {form.rego_date !== originalValues.rego_date && !form.registration_ordered && (
                  <p className="text-xs text-muted-foreground">Booking cleared (Rego date changed)</p>
                )}
              </div>
              {regoSuggestion && (
                <div className="space-y-2">
                  <Label>Suggested Renewal</Label>
                  <div className="rounded-md border border-yellow-900/30 bg-yellow-950/20 px-3 py-2 text-sm">
                    Buy <span className="font-medium text-yellow-500">{regoSuggestion.months} months</span> -- lands on{" "}
                    {formatDate(regoSuggestion.targetDate)} ({regoSuggestion.targetLabel}), currently{" "}
                    {regoSuggestion.targetCount} other vehicle{regoSuggestion.targetCount === 1 ? "" : "s"} due that month.
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
          )}
          {activeTab === "maintenance" && (
          <div>
          {/* Booked Maintenance */}
          <div>
            <h4 className="font-medium mb-1">Booked Maintenance</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Book a vehicle out for maintenance over a date range (e.g. items booked in
              advance). The vehicle stays available in the roster until the "From" date and
              is shown as unavailable through to the "To" date.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="maintenance_from">Maintenance From</Label>
                <Input
                  id="maintenance_from"
                  type="date"
                  value={form.maintenance_from || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((prev: any) => {
                      const next = { ...prev, maintenance_from: value };
                      // keep To >= From
                      if (value && prev.maintenance_to && prev.maintenance_to < value) {
                        next.maintenance_to = value;
                      }
                      return next;
                    });
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    const parsed = parseDateInput(pasted.trim());
                    if (parsed && parsed !== pasted.trim()) {
                      e.preventDefault();
                      setForm({ ...form, maintenance_from: parsed });
                    }
                  }}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maintenance_to">Maintenance To</Label>
                <Input
                  id="maintenance_to"
                  type="date"
                  value={form.maintenance_to || ""}
                  min={form.maintenance_from || undefined}
                  onChange={(e) => setForm({ ...form, maintenance_to: e.target.value })}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    const parsed = parseDateInput(pasted.trim());
                    if (parsed && parsed !== pasted.trim()) {
                      e.preventDefault();
                      setForm({ ...form, maintenance_to: parsed });
                    }
                  }}
                  disabled={!isAdmin}
                />
              </div>
            </div>
            {(form.maintenance_from || form.maintenance_to) && (
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-muted-foreground">
                  {form.maintenance_from && form.maintenance_to
                    ? `Booked out ${formatDate(form.maintenance_from)} – ${formatDate(
                        form.maintenance_to
                      )}`
                    : "Enter both a From and To date to set the maintenance window."}
                </p>
                {isAdmin && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      setForm({ ...form, maintenance_from: "", maintenance_to: "" })
                    }
                  >
                    Clear booking
                  </Button>
                )}
              </div>
            )}
          </div>
          </div>
          )}
          {activeTab === "odometer" && (
          <div className="space-y-4">
          {/* Odometer Thresholds */}
          <div>
            <h4 className="font-medium mb-3">Odometer Thresholds (km)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ruc_max">RUC Max</Label>
                <Input
                  id="ruc_max"
                  type="number"
                  value={form.ruc_max}
                  onChange={(e) => handleRucMaxChange(e.target.value)}
                  disabled={!isAdmin || rucInactive}
                  placeholder={rucInactive ? "N/A" : "e.g. 50000"}
                />
                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox
                    id="ruc_inactive"
                    checked={rucInactive}
                    onCheckedChange={(checked) => {
                      setRucInactive(!!checked);
                      if (checked) {
                        setForm({ ...form, ruc_max: "", ruc_ordered: "" });
                      }
                    }}
                    disabled={!isAdmin}
                  />
                  <Label htmlFor="ruc_inactive" className="text-sm text-muted-foreground cursor-pointer">
                    No RUC (N/A)
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ruc_ordered">RUC Ordered</Label>
                <Input
                  id="ruc_ordered"
                  type="date"
                  value={form.ruc_ordered}
                  onChange={(e) => setForm({ ...form, ruc_ordered: e.target.value })}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    const parsed = parseDateInput(pasted.trim());
                    if (parsed && parsed !== pasted.trim()) {
                      e.preventDefault();
                      setForm({ ...form, ruc_ordered: parsed });
                    }
                  }}
                  disabled={!isAdmin || rucInactive}
                />
                {form.ruc_max !== originalValues.ruc_max && !form.ruc_ordered && (
                  <p className="text-xs text-muted-foreground">Booking cleared (RUC changed)</p>
                )}
              </div>

            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="service_due">Service Due</Label>
                <Input
                  id="service_due"
                  type="number"
                  value={form.service_due}
                  onChange={(e) => handleServiceDueChange(e.target.value)}
                  disabled={!isAdmin || serviceInactive}
                  placeholder={serviceInactive ? "N/A" : "e.g. 15000"}
                />
                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox
                    id="service_inactive"
                    checked={serviceInactive}
                    onCheckedChange={(checked) => {
                      setServiceInactive(!!checked);
                      if (checked) {
                        setForm({ ...form, service_due: "", service_booked_for: "" });
                      }
                    }}
                    disabled={!isAdmin}
                  />
                  <Label htmlFor="service_inactive" className="text-sm text-muted-foreground cursor-pointer">
                    No Service Tracking (N/A)
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="service_booked_for">Service Booked For</Label>
                <Input
                  id="service_booked_for"
                  type="date"
                  value={form.service_booked_for}
                  onChange={(e) => setForm({ ...form, service_booked_for: e.target.value })}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    const parsed = parseDateInput(pasted.trim());
                    if (parsed && parsed !== pasted.trim()) {
                      e.preventDefault();
                      setForm({ ...form, service_booked_for: parsed });
                    }
                  }}
                  disabled={!isAdmin || serviceInactive}
                />
                {form.service_due !== originalValues.service_due && !form.service_booked_for && (
                  <p className="text-xs text-muted-foreground">Booking cleared (Service changed)</p>
                )}
              </div>

            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="tyre_rotation_due">Tyre Rotation Due</Label>
                <Input
                  id="tyre_rotation_due"
                  type="number"
                  value={form.tyre_rotation_due}
                  onChange={(e) => handleTyreRotationDueChange(e.target.value)}
                  disabled={!isAdmin || tyreInactive}
                  placeholder={tyreInactive ? "N/A" : "e.g. 10000"}
                />
                <div className="flex items-center space-x-2 pt-1">
                  <Checkbox
                    id="tyre_inactive"
                    checked={tyreInactive}
                    onCheckedChange={(checked) => {
                      setTyreInactive(!!checked);
                      if (checked) {
                        setForm({ ...form, tyre_rotation_due: "", tyre_rotation_booked_for: "" });
                      }
                    }}
                    disabled={!isAdmin}
                  />
                  <Label htmlFor="tyre_inactive" className="text-sm text-muted-foreground cursor-pointer">
                    No Tyre Rotation Tracking (N/A)
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tyre_rotation_booked_for">Tyre Rotation Booked For</Label>
                <Input
                  id="tyre_rotation_booked_for"
                  type="date"
                  value={form.tyre_rotation_booked_for}
                  onChange={(e) => setForm({ ...form, tyre_rotation_booked_for: e.target.value })}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    const parsed = parseDateInput(pasted.trim());
                    if (parsed && parsed !== pasted.trim()) {
                      e.preventDefault();
                      setForm({ ...form, tyre_rotation_booked_for: parsed });
                    }
                  }}
                  disabled={!isAdmin || tyreInactive}
                />
                {form.tyre_rotation_due !== originalValues.tyre_rotation_due && !form.tyre_rotation_booked_for && (
                  <p className="text-xs text-muted-foreground">Booking cleared (Tyre rotation changed)</p>
                )}
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-2">
            <h4 className="font-medium mb-3">Manual Odometer Entry</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Enter the current odometer reading for vehicles that need manual tracking.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="manual_km">Current KM</Label>
                <Input
                  id="manual_km"
                  type="number"
                  value={form.manual_km}
                  onChange={(e) => setForm({ ...form, manual_km: e.target.value })}
                  disabled={!isAdmin}
                  placeholder="e.g. 125000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manual_km_date">Date Recorded</Label>
                <Input
                  id="manual_km_date"
                  type="date"
                  value={form.manual_km_date}
                  onChange={(e) => setForm({ ...form, manual_km_date: e.target.value })}
                  disabled={!isAdmin}
                />
              </div>
            </div>
            {vehicle.manual_km && vehicle.manual_km_date && (
              <p className="text-sm text-muted-foreground mt-2">
                Last recorded: {vehicle.manual_km.toLocaleString()} km on {formatDate(vehicle.manual_km_date)}
                {vehicle.manual_km_driver ? ` (${vehicle.manual_km_driver}'s inspection)` : ""}
              </p>
            )}

          </div>
          </div>
          )}
          {activeTab === "cards" && (
          <div className="space-y-4">
          {/* Fuel Card Assignment */}
          <div>
            <h4 className="font-medium mb-3">Fuel Card Assignment</h4>
            <div className="space-y-2">
              <Label>Assigned Fuel Card</Label>
              <Popover open={cardPopoverOpen} onOpenChange={setCardPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={cardPopoverOpen}
                    className="w-full justify-between font-normal"
                    disabled={!isAdmin}
                  >
                    {selectedCardDisplay}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search fuel cards..." />
                    <CommandList>
                      <CommandEmpty>No fuel card found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__none__"
                          onSelect={() => {
                            setSelectedCardId("");
                            setCardPopoverOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              !selectedCardId ? "opacity-100" : "opacity-0"
                            )}
                          />
                          No fuel card
                        </CommandItem>
                        {selectableCards.map((card) => {
                          let display = card.pool_number;
                          if (card.card_type) display += ` (${card.card_type})`;
                          if (card.pin) display += ` - PIN: ${card.pin}`;
                          return (
                            <CommandItem
                              key={card.id}
                              value={display}
                              onSelect={() => {
                                setSelectedCardId(card.id);
                                setCardPopoverOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedCardId === card.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {display}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedCardId && selectedCardId !== currentFuelCardId && (
                <p className="text-sm text-muted-foreground">
                  Fuel card assignment will be updated when you save.
                </p>
              )}
            </div>
          </div>

          {/* EFTPOS Machine Assignment */}
          <div className="border-t pt-4 mt-2">
            <h4 className="font-medium mb-3">EFTPOS Machine Assignment</h4>
            <div className="space-y-2">
              <Label>Assigned EFTPOS Machine</Label>
              <Popover open={machinePopoverOpen} onOpenChange={setMachinePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={machinePopoverOpen}
                    className="w-full justify-between font-normal"
                    disabled={!isAdmin}
                  >
                    {selectedMachineDisplay}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search EFTPOS machines..." />
                    <CommandList>
                      <CommandEmpty>No EFTPOS machine found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__none__"
                          onSelect={() => {
                            setSelectedMachineId("");
                            setMachinePopoverOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              !selectedMachineId ? "opacity-100" : "opacity-0"
                            )}
                          />
                          No EFTPOS machine
                        </CommandItem>
                        {selectableMachines.map((machine) => {
                          let display = `SN ${machine.serial_number}`;
                          display += machine.terminal_id ? ` (TID ${machine.terminal_id})` : " (no Terminal ID on file)";
                          return (
                            <CommandItem
                              key={machine.id}
                              value={display}
                              onSelect={() => {
                                setSelectedMachineId(machine.id);
                                setMachinePopoverOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedMachineId === machine.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {display}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedMachineId !== (currentEftposMachineId ?? "") && (
                <p className="text-sm text-muted-foreground">
                  EFTPOS machine assignment (and this vehicle's EFT ID) will be updated when you save.
                </p>
              )}
            </div>
          </div>
          </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {isAdmin && (
            <Button onClick={saveVehicle} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <BillableUnitsWarningDialog {...billableUnitsDialogProps} />
    </>
  );
}
